import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import './splash-screen.scss';

// Sentinel keys must stay in sync with redirectIfDeploymentMismatch() in
// src/actions/auth.js. The sentinel is set on the source deployment just
// before window.location.assign('/app'), so the destination renders this
// branded overlay instead of a blank white page during the post-auth reload.
const SENTINEL_KEY = 'm8_post_auth_transition';
const DISPLAY_NAME_KEY = 'm8_post_auth_display_name';

const readSentinel = () => {
  try {
    return sessionStorage.getItem(SENTINEL_KEY) === '1';
  } catch (_) {
    return false;
  }
};

const readDisplayName = () => {
  try {
    return sessionStorage.getItem(DISPLAY_NAME_KEY) || '';
  } catch (_) {
    return '';
  }
};

const clearSentinel = () => {
  try {
    sessionStorage.removeItem(SENTINEL_KEY);
    sessionStorage.removeItem(DISPLAY_NAME_KEY);
  } catch (_) {
    // sessionStorage may be unavailable; the sentinel will simply persist
    // until the tab closes, which is harmless.
  }
};

const SplashScreen = ({ authReady }) => {
  const [active, setActive] = useState(() => readSentinel());
  const [fadingOut, setFadingOut] = useState(false);
  const [displayName] = useState(() => readDisplayName());

  // Safety timeout — never leave the splash up for more than 8s even if
  // auth never hydrates (e.g. backend slow, network glitch). Falling back
  // to the underlying app is better than a stuck overlay. We also clear the
  // sentinel here as a belt-and-suspenders cleanup: if the CSS transition is
  // skipped (prefers-reduced-motion, component unmounted mid-fade, browser
  // quirk), `onTransitionEnd` may never fire, and without this the sentinel
  // would persist and re-show the splash on the next page load.
  useEffect(() => {
    if (!active) return undefined;
    const timer = window.setTimeout(() => {
      setFadingOut(true);
      clearSentinel();
    }, 8000);
    return () => window.clearTimeout(timer);
  }, [active]);

  useEffect(() => {
    if (!active) return;
    if (authReady && !fadingOut) {
      setFadingOut(true);
    }
  }, [active, authReady, fadingOut]);

  const onTransitionEnd = (event) => {
    if (event.propertyName !== 'opacity') return;
    if (!fadingOut) return;
    clearSentinel();
    setActive(false);
  };

  if (!active) return null;

  const greeting = displayName ? `Welcome back, ${displayName}` : 'Setting up your workspace';

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Loading your workspace"
      className={`m8-splash-screen${fadingOut ? ' is-fading-out' : ''}`}
      onTransitionEnd={onTransitionEnd}
    >
      <div className="m8-splash-screen__inner">
        <div className="m8-splash-screen__logo" aria-hidden="true">M</div>
        <div className="m8-splash-screen__title">{greeting}</div>
        <div className="m8-splash-screen__subtitle">One moment while we get things ready…</div>
        <div className="m8-splash-screen__spinner" aria-hidden="true" />
      </div>
    </div>
  );
};

SplashScreen.propTypes = {
  authReady: PropTypes.bool,
};

SplashScreen.defaultProps = {
  authReady: false,
};

export default SplashScreen;
